import React, { useState, ChangeEvent } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Grid,
    Typography,
} from '@mui/material';
import { editUser } from 'src/api/userManagement';
import { AuthTypes, UserForm, EditUserRequestBody } from './types';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';

interface CreateUserDialogProps {
    open: boolean;
    onClose: () => void;
    onUserCreated?: () => void;
}

export default function CreateUserDialog({ open, onClose, onUserCreated }: CreateUserDialogProps) {
    const blankUserForm: UserForm = {
        firstName: '',
        lastName: '',
        email: '',
        isFederated: false,
    };

    const [userForm, setUserForm] = useState<UserForm>({ ...blankUserForm });
    const [formErrors, setFormErrors] = useState<Partial<UserForm>>({});
    const [emailExists, setEmailExists] = useState(false);

    const { mutateAsync: createUserAsync, isPending: isCreateUserPending } = editUser(userForm, true);

    const onChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setUserForm({ ...userForm, [name]: value });
    };

    const onChangeBooleanSelection = (e: SelectChangeEvent<AuthTypes>) => {
        const { name, value } = e.target;
        setUserForm({ ...userForm, [name]: value === AuthTypes.External });
    };

    const validateFields = (): Partial<UserForm> => {
        const tempFormErrors: Partial<UserForm> = {};

        if (!userForm.firstName) {
            tempFormErrors.firstName = 'First Name cannot be blank.';
        }

        if (!userForm.lastName) {
            tempFormErrors.lastName = 'Last Name cannot be blank.';
        }

        if (!userForm.email) {
            tempFormErrors.email = 'Email Address cannot be blank.';
        } else {
            const emailRegex = /^[a-zA-Z0-9'._+-]+@[a-zA-Z0-9'._+-]+\.[a-zA-Z0-9'._+-]*$/;
            if (!emailRegex.test(userForm.email)) {
                tempFormErrors.email = 'Invalid Email address';
            }
        }

        setEmailExists(false);
        setFormErrors(tempFormErrors);
        return tempFormErrors;
    };

    const handleSave = async () => {
        const errors = validateFields();
        if (!errors.firstName && !errors.lastName && !errors.email) {
            try {
                await createUserAsync({ ...userForm } as EditUserRequestBody);
                if (onUserCreated) {
                    onUserCreated();
                }
                setUserForm({ ...blankUserForm });
                onClose();
            } catch (err) {
                setEmailExists(true);
            }
        }
    };

    const handleCancel = () => {
        onClose();
        setUserForm({ ...blankUserForm });
    };

    return (
        <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
            <DialogTitle>Create New User</DialogTitle>
            <DialogContent>
                {isCreateUserPending && <LoadingIndicator message="Creating User..." />}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.firstName}
                                helperText={formErrors.firstName}
                                required
                                id="firstName"
                                name="firstName"
                                label="First Name"
                                variant="outlined"
                                value={userForm.firstName}
                                onChange={onChangeInput}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.lastName}
                                helperText={formErrors.lastName}
                                required
                                id="lastName"
                                name="lastName"
                                label="Last Name"
                                variant="outlined"
                                value={userForm.lastName}
                                onChange={onChangeInput}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                required
                                error={!!formErrors.email}
                                helperText={formErrors.email}
                                id="email"
                                name="email"
                                label="Email Address"
                                variant="outlined"
                                value={userForm.email}
                                onChange={onChangeInput}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="federated-select-label">Authentication</InputLabel>
                            <Select
                                labelId="federated-select-label"
                                label="Authentication"
                                required
                                error={!!formErrors.isFederated}
                                id="isFederated"
                                name="isFederated"
                                value={userForm.isFederated ? AuthTypes.External : AuthTypes.Internal}
                                onChange={onChangeBooleanSelection}
                            >
                                <MenuItem value={AuthTypes.Internal}>Internal FMTP</MenuItem>
                                <MenuItem value={AuthTypes.External}>External Microsoft Entra</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {emailExists && (
                        <Grid item xs={12}>
                            <Typography color="error">
                                A user with that email address already exists. Please use a different email address.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={isCreateUserPending}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
